import type { CollectionCreateSchema } from "../types/typesense"
/**
 * Schema Registry — Maps ALL Typesense collection names to their CollectionCreateSchema.
 *
 * Used by the search stack's custom resource handler to recreate collections
 * when they are deleted (404 recovery). Single source of truth for all collection schemas.
 */

import { agencySchema } from "../schemas/agency-schema"
import { bottleSizesSchema } from "../schemas/bottle-sizes-schema"
import { businessAreasSchema } from "../schemas/business-areas-schema"
import { captionSchema } from "../schemas/caption-schema"
import { certificateDeliveryMethodsSchema } from "../schemas/certificate-delivery-methods-schema"
import { certificateTypesSchema } from "../schemas/certificate-types-schema"
import { cnCodesSchema } from "../schemas/cn-codes-schema"
import { commodityTypesSchema } from "../schemas/commodity-types-schema"
import { countriesSchema } from "../schemas/countries-schema"
import { deAlcoholisedSchema } from "../schemas/de-alcoholised-schema"
import { destinationMarketsSchema } from "../schemas/destination-markets-schema"
import { documentTypesSchema } from "../schemas/document-types-schema"
import { eligibilitiesSchema } from "../schemas/eligibilities-schema"
import { euWineIngredientsSchema } from "../schemas/eu-wine-ingredients-schema"
import { exportCertificatesSchema } from "../schemas/export-certificates-schema"
import { fieldSchema } from "../schemas/field-schema"
import { hsCodesSchema } from "../schemas/hs-codes-schema"
import { importCertificateTypesSchema } from "../schemas/import-certificate-types-schema"
import { ippcAcronymsSchema } from "../schemas/ippc-acronyms-schema"
import { itemHistorySchema } from "../schemas/item-history-schema"
import { itemSchema } from "../schemas/item-schema"
import { languageSchema } from "../schemas/language-schema"
import { listSchema } from "../schemas/list-schema"
import { packageTypesSchema } from "../schemas/package-types-schema"
import { ocaUsersSchema } from "../schemas/oca-users-schema"
import { pesticidesSchema } from "../schemas/pesticides-schema"
import { phytoBotanicalNameSchema } from "../schemas/phyto-botanical-name-schema"
import { portSchema } from "../schemas/port-schema"
import { processGroupCaptionSchema } from "../schemas/process-group-caption-schema"
import { processGroupSchema } from "../schemas/process-group-schema"
import { processSchema } from "../schemas/process-schema"
import { processTypesSchema } from "../schemas/process-types-schema"
import { productFieldsSchema } from "../schemas/product-fields-schema"
import { productsSchema } from "../schemas/products-schema"
import { registrationsSchema } from "../schemas/registrations-schema"
import { supportingDocumentsSchema } from "../schemas/supporting-documents-schema"
import { templatesSchema } from "../schemas/templates-schema"
import { transferProductsSchema } from "../schemas/transfer-products-schema"
import { transferRequestsSchema } from "../schemas/transfer-requests-schema"
import { unitSchema } from "../schemas/unit-schema"
import { unLocodeSchema } from "../schemas/un-locode-schema"
import { wineBrClassificationsSchema } from "../schemas/wine-br-classifications-schema"
import { wineChemTestsSchema } from "../schemas/wine-chem-tests-schema"
import { wineColoursSchema } from "../schemas/wine-colours-schema"
import { wineEuCategoriesSchema } from "../schemas/wine-eu-categories-schema"
import { wineGeographicIndicationsSchema } from "../schemas/wine-geographic-indications-schema"
import { wineGrapeVarietiesSchema } from "../schemas/wine-grape-varieties-schema"
import { wineLabDetailSchema } from "../schemas/wine-lab-detail-schema"
import { wineSpecialProcessesSchema } from "../schemas/wine-special-processes-schema"
import { wineStylesSchema } from "../schemas/wine-styles-schema"

/** All schemas — the name field in each schema is the map key */
const ALL_SCHEMAS: CollectionCreateSchema[] = [
  agencySchema,
  bottleSizesSchema,
  businessAreasSchema,
  captionSchema,
  certificateDeliveryMethodsSchema,
  certificateTypesSchema,
  cnCodesSchema,
  commodityTypesSchema,
  countriesSchema,
  deAlcoholisedSchema,
  destinationMarketsSchema,
  documentTypesSchema,
  eligibilitiesSchema,
  euWineIngredientsSchema,
  exportCertificatesSchema,
  fieldSchema,
  hsCodesSchema,
  importCertificateTypesSchema,
  ippcAcronymsSchema,
  itemHistorySchema,
  itemSchema,
  languageSchema,
  listSchema,
  packageTypesSchema,
  ocaUsersSchema,
  pesticidesSchema,
  phytoBotanicalNameSchema,
  portSchema,
  processGroupCaptionSchema,
  processGroupSchema,
  processSchema,
  processTypesSchema,
  productFieldsSchema,
  productsSchema,
  registrationsSchema,
  supportingDocumentsSchema,
  templatesSchema,
  transferProductsSchema,
  transferRequestsSchema,
  unitSchema,
  unLocodeSchema,
  wineBrClassificationsSchema,
  wineChemTestsSchema,
  wineColoursSchema,
  wineEuCategoriesSchema,
  wineGeographicIndicationsSchema,
  wineGrapeVarietiesSchema,
  wineLabDetailSchema,
  wineSpecialProcessesSchema,
  wineStylesSchema,
]

const SCHEMA_MAP = new Map<string, CollectionCreateSchema>(
  ALL_SCHEMAS.map((s) => [s.name, s]),
)

/** Get the schema for a collection by name. Throws if not found. */
export const getCollectionSchema = (
  collectionName: string,
): CollectionCreateSchema => {
  const schema = SCHEMA_MAP.get(collectionName)
  if (!schema)
    throw new Error(`Schema not found for collection "${collectionName}"`)
  return schema
}

/**
 * Get a ready-to-use Typesense schema with metadata.prefix set from visibility.
 * Can be passed directly to client.collections().create() or createCollection().
 */
export const getCollectionSchemaWithPrefix = (
  collectionName: string,
): CollectionCreateSchema => {
  const schema = getCollectionSchema(collectionName)
  return { ...schema, metadata: { prefix: schema.visibility ?? "public" } }
}

/** Get all registered collection names that have schemas */
export const getAllSchemaNames = (): string[] => [...SCHEMA_MAP.keys()]

/** Get the prefixed alias for a collection, e.g. "public:port" */
export const collectionAlias = (name: string): string => {
  const schema = getCollectionSchema(name)
  return `${schema.visibility ?? "public"}:${schema.name}`
}
